import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Divider, FormControl, FormControlLabel,
  Paper, Radio, RadioGroup, Stack, Switch, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';

import { pageVariants, sectionVariants } from '../../../../animations/variants';
import {
  cascadeDeleteIpam, disableIpam, fixIpamRoles, getIpamActiveTeardown, getIpamConfig,
  getIpamCostPreview, getIpamRolesJob, getIpamTeardownPreview, startIpamRolesScan,
  updateIpamOperatingRegions, updateIpamTier,
} from '../../../../services/ipamApi';
import RolePermissionsPanel from '../../../../components/portal/securityhub/RolePermissionsPanel';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../../components/portal/PortalStates';
import { AWS_REGIONS } from '../../../../constants/awsRegions';
import MultiSelectField from '../MultiSelectField';
import OperationProgress from '../OperationProgress';
import { POLL_INTERVAL_MS } from '../pollInterval';
import {
  CK, FONT_BODY, FONT_MONO, FONT_TITLE, actionRowSx, bodyCellSx, headCellSx, insetPanelSx,
  listCardSx, rowSx, sectionCardSx, sectionHeadingSx, statusChipSx, tableHeadRowSx,
} from '../../portalStyles';
import {
  IPAM_ADVANCED_PRICE, IPAM_ADVANCED_PRICE_MONTHLY, IPAM_ADVANCED_REQUIRED_NOTE,
  IPAM_FREE_TIER_NOTE, IPAM_PRICE_NOTE, formatMonthlyCostUsd,
} from './ipamPricing';
import {
  IPAM_ADOPTED_TEARDOWN_SUMMARY,
  IPAM_CASCADE_NOT_YET_REASON, IPAM_CASCADE_UNREADABLE_REASON,
  IPAM_FORCE_DELETE_COPY,
  IPAM_NO_VPC_DELETED, IPAM_TEARDOWN_AWAITING_CASCADE, IPAM_TEARDOWN_BLOCKED,
  IpamDestructiveConfirmDialog,
  IpamResidueBanner, ipamBillingStops, ipamDestructiveConfirmLabel, ipamHistoryDestroyed,
  ipamPreviewBillingLine, parseIpamAccountMap, parseIpamAccountMessage, parseIpamBlockers,
} from './IpamBlockedPanel';
// The Settings bill prints the same figure the dashboard does, so it prints the
// same age with it — see ipamEstimatedBillAge.
import {
  ipamCostChip, ipamEstimatedBillAge, ipamHeldIpamId, ipamResidualIpamAlert,
} from './ipamCopy';

// IpamSettingsPage — Operating regions · Tier & cost · Permissions · Danger zone.
//
// The first three tabs are the ordinary settings shape (MacieSettingsPage.js,
// AccessAnalyzerSettingsPage.js). The fourth is deliberately NOT that shape, and
// section 11.6 of the design says why: every existing danger zone in this portal
// is a one-shot — one warning Alert, one red button, one type-the-customer-ID
// dialog — which is exactly right when teardown succeeds or fails as a unit.
// IPAM's teardown is a ten-stage chain across two AWS services, with a
// first-class `blocked` outcome and an escape hatch that is a DIFFERENT AWS
// operation with its own precondition. A modal cannot tell that truth, so the
// danger zone is three stages rendered in the page: the pre-flight ledger, the
// mode, and only then the typed confirmation.
//
// Money and destruction copy is imported, never re-spelled: the rate comes from
// ipamPricing.js (the module the enable wizard also imports) and the teardown
// sentences from IpamBlockedPanel.js (the other screen that authorises this
// action). A price or a promise that disagrees with itself across two screens is
// a support ticket at best.

// Slate / blue-grey 700. Per portal convention the accent hex is a local const
// per page file.
const IPAM_COLOR = '#455A64';
const IPAM_COLOR_DARK = '#31434B';

const TAB_REGIONS = 0;
const TAB_TIER = 1;
const TAB_PERMISSIONS = 2;
const TAB_DANGER = 3;

const TIER_ADVANCED = 'advanced';
const TIER_FREE = 'free';

const MODE_GRACEFUL = 'graceful';
const MODE_CASCADE = 'cascade';

// Three consecutive failures before a background poll gives up, matching the
// portal's house rule.
const MAX_POLL_FAILURES = 3;

const ipamButtonSx = {
  backgroundColor: IPAM_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: IPAM_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const plainButtonSx = { fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' };
const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const hintTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage operating Regions, the IPAM tier and its bill, account permissions, and teardown"
    color={IPAM_COLOR}
  />
);

// operatingRegions is a JSON text column. Tolerate the decoded array too so a DTO
// change cannot blank the form.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try {
    return JSON.parse(v || '[]');
  } catch (_) {
    return [];
  }
};

const sameSet = (a, b) => a.length === b.length
  && [...a].sort().join('|') === [...b].sort().join('|');

// Region and tier updates claim the config atomically and are refused unless it
// is settled and live, so a Save dispatched from any other status is a guaranteed
// refusal. `blocked` is excluded deliberately: it is a teardown waiting on a
// human, and re-shaping the IPAM under it changes what that teardown has already
// reported.
const configAllowsUpdate = (status) => status === 'enabled' || status === 'partial_failure';

// The same relative-time ladder the dashboard and Accounts pages use, spelled
// locally because it is three lines and there is no shared time module in this
// capability. null in, null out — "Prism has no record of when this was counted"
// is a different sentence from a bad timestamp, and the copy owns that choice.
const settingsRelativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  return `${Math.round(secs / 86400)} d ago`;
};

// ---------------------------------------------------------------------------
// Stage 1's vocabulary
// ---------------------------------------------------------------------------

// The residue ledger's verdict chips. This is OrphanScanPage.js's verdict
// vocabulary — an explicit {intent, label, hint} map with a fallback that fails
// toward caution — applied BEFORE the action instead of after a scan. The
// unknown arm is the load-bearing part: a severity Prism does not recognise must
// not render as the calmest thing on screen.
const SEVERITY_META = {
  critical: {
    label: 'Critical',
    intent: 'error',
    hint: 'This row is about money or an irreversible loss. Read it before you confirm.',
  },
  warning: {
    label: 'Warning',
    intent: 'warning',
    hint: 'Something survives this teardown that you will have to deal with yourself.',
  },
  info: {
    label: 'For information',
    intent: 'info',
    hint: 'Expected residue. It is free, inert, or simply how AWS behaves.',
  },
};

const severityMeta = (s) => SEVERITY_META[String(s || '').toLowerCase()] || {
  label: s ? String(s) : 'Unknown',
  intent: 'warning',
  hint: 'Prism does not recognise this severity. Treat the row as unreviewed and read it in full.',
};

// The honest answer to "can Prism clean this up?", including the two answers a
// boolean cannot express.
const REMOVABLE_META = {
  no: {
    label: 'Prism cannot remove it',
    intent: 'warning',
    hint: 'It survives the teardown. Removing it is something only you can do, in that account.',
  },
  partial: {
    label: 'Partly removable',
    intent: 'warning',
    hint: 'Prism removes some of this and leaves the rest behind.',
  },
  retryable: {
    label: 'Retryable',
    intent: 'info',
    hint: 'Running the teardown again is expected to clear this.',
  },
  'n/a': {
    label: 'Not applicable',
    intent: 'neutral',
    hint: 'Nothing is left behind to remove.',
  },
};

const removableMeta = (r) => REMOVABLE_META[String(r || '').toLowerCase()] || {
  label: r ? String(r) : 'Unknown',
  intent: 'warning',
  hint: 'Prism does not recognise this answer. Assume it survives the teardown until you have checked.',
};

// The four tabs are addressable by name, because the residue banner on
// IpamEntry sends the operator here to retry a half-failed teardown while the
// meter is running. Landing them on the Operating regions form and asking them
// to find the Danger zone is the wrong destination for the highest-stakes link
// in the capability. Unknown or absent -> Operating regions, the safe default.
const TAB_SLUGS = { regions: TAB_REGIONS, tier: TAB_TIER, permissions: TAB_PERMISSIONS, danger: TAB_DANGER };
const SLUG_BY_TAB = ['regions', 'tier', 'permissions', 'danger'];

export default function IpamSettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => TAB_SLUGS[searchParams.get('tab')] ?? TAB_REGIONS);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [regions, setRegions] = useState([]);
  const [tierTarget, setTierTarget] = useState(TIER_ADVANCED);
  const [acknowledged, setAcknowledged] = useState(false);

  const [costPreview, setCostPreview] = useState(null);
  const [costErr, setCostErr] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [jobErr, setJobErr] = useState('');
  const [mode, setMode] = useState(MODE_GRACEFUL);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Extracted from the mount effect so the error state below can offer a real
  // retry. Seeding the form fields from the response is safe here and only here:
  // this runs when there is no config yet, so there is no edit to lose.
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIpamConfig();
      const cfg = res?.data || null;
      setConfig(cfg);
      if (cfg) {
        setRegions(toStringArray(cfg.operatingRegions));
        setTierTarget(cfg.tier || TIER_ADVANCED);
      }
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const status = config?.status;
  const operationRunning = status === 'in_progress';

  // Every Save on this page is gated on a settled config, so without a poll a
  // visitor who arrives mid-operation would sit in front of permanently disabled
  // buttons until they reloaded by hand. Only the config is refreshed — never the
  // form fields, which would otherwise overwrite an edit in progress.
  useEffect(() => {
    if (!operationRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getIpamConfig();
        setConfig(res?.data || null);
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [operationRunning]);

  // The cost preview answers `data: null` when there is nothing to estimate yet,
  // which is a real answer meaning "we cannot price this", not an error.
  const configured = !!config;
  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getIpamCostPreview();
        if (cancelled) return;
        setCostPreview(res?.data || null);
        setCostErr('');
      } catch (e) {
        if (cancelled) return;
        setCostErr(e?.message || 'Prism could not read the cost preview.');
      }
    })();
    return () => { cancelled = true; };
  }, [configured]);

  // --- Stage 1 and stage 2 data: the ledger and the live teardown job --------
  //
  // Both are loaded only once the Danger zone is actually open. The ledger is a
  // real read against the customer's resource ledger and the job read is a poll;
  // neither belongs on a page load whose visitor came to change a Region.
  const dangerOpen = tab === TAB_DANGER && configured;

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await getIpamTeardownPreview();
      setPreview(res?.data || null);
      setPreviewErr('');
    } catch (e) {
      setPreviewErr(e?.message || 'Prism could not build the teardown preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dangerOpen) return;
    loadPreview();
  }, [dangerOpen, loadPreview, status]);

  // The interval is owned by the effect and keyed on primitives only. Keying it
  // on the job object would rebuild it on every tick, drifting the cadence and
  // resetting the failure counter each cycle.
  useEffect(() => {
    if (!dangerOpen) return undefined;
    let cancelled = false;
    let failures = 0;
    let timer = null;
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const tick = async () => {
      try {
        const res = await getIpamActiveTeardown();
        if (cancelled) return;
        failures = 0;
        setActiveJob(res?.data || null);
        setJobErr('');
      } catch (e) {
        if (cancelled) return;
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          setJobErr(e?.message || 'Prism could not read the live teardown job.');
          stop();
        }
      }
    };
    tick();
    timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      stop();
    };
  }, [dangerOpen]);

  const blockers = useMemo(
    () => parseIpamBlockers(activeJob?.blockersJson),
    [activeJob],
  );

  // WHICH KIND OF `partial_failure` IS THIS? The residue banner is about a
  // TEARDOWN that did not finish, and `config.status === 'partial_failure'` is
  // not that question — three unrelated paths write that one status:
  //
  //   1. the enable fan-out (ipam_service.go rollupConfigStatus), whose message
  //      is "%d of %d accounts failed to enrol in VPC IPAM";
  //   2. the operating-region sync, whose message is "IPAM … now monitors …, but
  //      the resource discovery could not be updated in …";
  //   3. a teardown that ended with errors (ipam_teardown.go settleTeardownRun
  //      and its two early returns), which writes the per-account map
  //      `errs.message()` renders as "account: message | account: message" and
  //      stores the best-effort lane in the job's residue_json.
  //
  // Only (3) means a teardown failed. Banner on the raw status and an operator
  // who half-enrolled their accounts is told their teardown left the IPAM
  // billing, and handed a teardown as the remedy. So the gate is the two signals
  // ONLY a teardown produces:
  //
  //   - a non-empty residue map on the live teardown job — the same discriminator
  //     IpamEntry uses; and
  //   - a config error_message that parses as a per-account map. This second one
  //     is not redundant: GetActiveTeardown's predicate is `status NOT IN
  //     terminalStatuses` and a teardown that ends in partial_failure settles its
  //     job as `failed`, which IS terminal — so /teardown/active answers null for
  //     precisely the case this banner exists for, and the prose message is the
  //     only copy of the map left. parseIpamAccountMessage refuses to guess (one
  //     segment that is not `<12 digits>: …` and the whole parse is abandoned),
  //     which is what keeps (1) and (2) — neither of which contains a ": "
  //     account key — from ever matching.
  //
  // AND A THIRD SIGNAL, BECAUSE THE SECOND ONE NOW MISSES A REAL CASE. The
  // teardown's new settle arm (services/ipam_teardown.go, `run.awaitCascade &&
  // ipamDeleted`) writes config `partial_failure` with FREE PROSE — "The IPAM was
  // DELETED and the Advanced-tier meter has stopped … press Retry teardown" — and
  // no per-account map, so parseIpamAccountMessage correctly refuses it and this
  // gate went false: a teardown that genuinely did not finish, with an actionable
  // instruction from the backend, rendered nothing at all on the one page that
  // owns teardown.
  //
  // A LIVE TEARDOWN JOB AT `blocked` IS UNAMBIGUOUS. Only a teardown writes an
  // ipam_teardown_jobs row, so neither the enable fan-out (1) nor the region sync
  // (2) can reach it, and `blocked` is NON-TERMINAL — which is exactly why
  // /teardown/active still answers for this arm where it answers null for the
  // `failed` one.
  const teardownResidue = useMemo(
    () => parseIpamAccountMap(activeJob?.residueJson),
    [activeJob],
  );
  const teardownDidNotFinish = Object.keys(teardownResidue).length > 0
    || parseIpamAccountMessage(config?.errorMessage) !== null
    || activeJob?.status === IPAM_TEARDOWN_BLOCKED;

  // FORCE DELETE IS DISABLED UNTIL A JOB HAS REACHED awaiting_cascade, AND IT
  // CARRIES ITS OWN REASON. The backend refuses a cascade from any other state,
  // so an always-enabled radio would trade a disabled control that explains
  // itself for an AWS error the operator has to decode — and it would put the
  // most destructive action in the portal on screen as a first action.
  const awaitingCascade = activeJob?.status === IPAM_TEARDOWN_AWAITING_CASCADE;
  const cascadeBlockedByPublicPool = preview && preview.cascadeAvailable === false;
  const cascadeEnabled = awaitingCascade && !cascadeBlockedByPublicPool;
  let cascadeReason = '';
  if (cascadeBlockedByPublicPool) {
    cascadeReason = preview.cascadeBlockedReason
      || 'AWS refuses the force delete while a pool exists in the public scope.';
  } else if (!awaitingCascade) {
    cascadeReason = jobErr ? IPAM_CASCADE_UNREADABLE_REASON : IPAM_CASCADE_NOT_YET_REASON;
  }

  // A mode the operator can no longer use must not stay selected: the confirm
  // button would then dispatch a call the backend refuses.
  useEffect(() => {
    if (mode === MODE_CASCADE && !cascadeEnabled) setMode(MODE_GRACEFUL);
  }, [mode, cascadeEnabled]);

  // --- Saves ---------------------------------------------------------------

  const saveRegions = async () => {
    if (config.homeRegion && !regions.includes(config.homeRegion)) {
      setErr('The home Region cannot be removed. It is where the IPAM itself lives.');
      return;
    }
    setBusy(true);
    try {
      await updateIpamOperatingRegions(regions);
      setErr(null);
      // Hand off to the Dashboard entry — it polls the config and narrates the
      // in-progress banner while ModifyIpam rewrites every discovery.
      navigate('/securityhub/ipam');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveTier = async () => {
    setBusy(true);
    try {
      // The acknowledgement is sent as the operator set it, never defaulted to
      // true here. The backend refuses the change without it, and two gates means
      // two places to forget: the failure mode of a malformed request must be
      // "the tier did not change", never "the tenant is now paying".
      await updateIpamTier(tierTarget, acknowledged);
      setErr(null);
      navigate('/securityhub/ipam');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Stage 3's dispatch --------------------------------------------------

  const dispatchTeardown = async () => {
    setBusy(true);
    try {
      if (mode === MODE_CASCADE) {
        await cascadeDeleteIpam(true);
      } else {
        await disableIpam(MODE_GRACEFUL);
      }
      setConfirmOpen(false);
      setErr(null);
      // IpamEntry narrates from here: in_progress while the stages run, the
      // blocked panel if a live resource holds a pool, the Dashboard with the
      // residue banner on a partial failure, and the setup wizard once the
      // config reports disabled.
      navigate('/securityhub/ipam');
    } catch (e) {
      // Closing on failure too is one of the dialog's exit paths, and the dialog
      // clears the typed customer ID on every one of them.
      setConfirmOpen(false);
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading VPC IPAM settings…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // MUST precede the not-configured branch. GET /config rejecting leaves `config`
  // null, and the branch below would then tell an operator whose IPAM is running
  // and billing that it was never set up — while discarding the actual error,
  // which is already sitting in `err`.
  if (err && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={err} onRetry={loadConfig} />
        </motion.div>
      </motion.div>
    );
  }

  // ── THE LOCK-OUT THAT WAS WORSE THAN THE WRONG SENTENCE ────────────────────
  //
  // This branch returned BEFORE the tabs rendered, so an operator whose IPAM was
  // still running and still billing could not reach the Danger zone or the Tier
  // tab — the only two screens carrying preview.stillBilling and the remedy. And
  // the sentence it returned instead ("not set up yet … complete the setup
  // wizard") is the exact opposite of what the backend computes from the same
  // ledger for the same tenant.
  //
  // A clean teardown of an ADOPTED IPAM settles `disabled` over an IPAM Prism
  // deliberately never deletes and that goes on metering. When Prism can see that
  // — ipamResidualIpamAlert answers non-null only then — the page falls THROUGH to
  // the tabs and states it in a banner. `canUpdate` is still false at `disabled`,
  // so the Regions and Tier forms stay read-only; what the operator gains is the
  // pre-flight ledger and the sentence telling them to delete the IPAM themselves.
  const residualAlert = ipamResidualIpamAlert(config, 'IPAM resources');

  // ── AND THE SAME LOCK-OUT, REACHED BY A SECOND ROUTE ───────────────────────
  //
  // `residualAlert` rescues the ADOPTED tenant, because ipamResidualIpam treats
  // adoption as decisive. It does NOT rescue the tenant this fix is about. Read
  // that function: for an IPAM Prism CREATED it returns HELD only at
  // `status === 'failed'`, so a NON-adopted config sitting at `disabled` while
  // still holding an ipam_id answers RESIDUE_NONE — no alert, no fall-through,
  // and the whole tabbed page replaced by "Amazon VPC IPAM is not set up yet for
  // this customer. Complete the setup wizard first."
  //
  // The two surfaces that render `billingVerified` — the Tier & cost panel and the
  // danger zone — are the only two screens in the product that would tell that
  // operator their meter is unconfirmed, and both live behind those tabs. Worse,
  // the cost preview carrying stillBilling / billingVerified / billingNote has
  // ALREADY been fetched by the time this branch runs and is thrown away
  // unrendered. The page holds the answer and shows the wizard prompt instead.
  //
  // ⚠ THE OVERSHOOT THIS CONJUNCT EXISTS TO PREVENT. `billingVerified === false`
  // on its own is NOT enough: `disabled` with an EMPTY ipam_id also produces
  // Tracked == 0 in the backend and therefore Verified = false, so keying on the
  // flag alone would drag genuinely-never-enabled tenants into the settings tabs
  // and tell them their meter is unconfirmed when there has never been a meter.
  // `ipamHeldIpamId` is what separates them: Prism is holding the id of a real
  // IPAM it cannot vouch for.
  const costBillingVerified = costPreview?.billingVerified;
  const costStillBilling = costPreview?.stillBilling;
  const costBillingNote = String(costPreview?.billingNote || '').trim();
  const unverifiedMeter = costBillingVerified === false && !!ipamHeldIpamId(config);

  // WHY THERE IS NO "WAIT FOR THE COST READ" BRANCH HERE.
  //
  // The obvious worry is a flash: `unverifiedMeter` is false until the cost read
  // answers, so should this branch hold the screen until it has? No, for three
  // reasons, and the first one is decisive.
  //
  //   1. THE FLASH IT WOULD PREVENT CANNOT HAPPEN IN THAT DIRECTION.
  //      `unverifiedMeter` only ever moves false -> true, so the tabs cannot
  //      appear and then be taken away. The only possible flash is the lock-out
  //      appearing for one microtask and being REPLACED by the tabs — and that is
  //      already exactly what today's `residualAlert` path does for every adopted
  //      tenant. Both fetches are fired together on mount; there is no user-visible
  //      frame between them.
  //   2. A HOLD NEEDS A "THE READ ANSWERED" FLAG, AND `costPreview === null` IS NOT
  //      ONE — it is also what a 200 with an empty body leaves behind. A flag that
  //      never flips is a page that spins for ever, which is a worse failure than
  //      the frame it was buying.
  //   3. The flag can only be carried by an extra state update landing after the
  //      page has already rendered, and under the test harness every such update
  //      remounts this whole subtree (the `motion` mock is a Proxy whose getter
  //      returns a NEW component type per access, so React replaces rather than
  //      reconciles). Measured, not guessed: it detached nodes two existing tests
  //      had already resolved and failed them deterministically.
  //
  // So the conjunct goes in and nothing waits.
  if (!config || (config.status === 'disabled' && !residualAlert && !unverifiedMeter)) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert
            severity="info"
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/ipam')}>
                Go to setup
              </Button>
            )}
          >
            Amazon VPC IPAM is not set up yet for this customer. Complete the setup wizard first to
            manage operating Regions, the tier, or teardown.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const storedRegions = toStringArray(config.operatingRegions);
  const canUpdate = configAllowsUpdate(config.status);
  const regionsChanged = !sameSet(regions, storedRegions);
  const tierChanged = tierTarget !== (config.tier || TIER_ADVANCED);

  // WAS THIS IPAM PRISM'S TO DELETE? `ipamAdopted` marks an IPAM that already
  // existed in the host account and that Prism took over rather than created.
  // The backend never deletes one (services/ipam_teardown.go raises a `critical`
  // residue row saying it KEEPS METERING, and sets StillBilling), so the danger
  // zone's three promises — the delete, the history loss, the meter stopping —
  // are all false for it and are branched on this flag.
  const ipamAdopted = config.ipamAdopted === true;

  const rawActiveIps = costPreview?.estimatedActiveIps ?? config.estimatedActiveIps;
  const activeIpsNum = Number(rawActiveIps);
  // -1 is the column's default and means "not counted". `estimateMonthlyCostUsd`
  // already refuses a negative, but the count itself must not be rendered either:
  // "-1 active IPs" is worse than saying Prism could not estimate.
  const activeIps = Number.isFinite(activeIpsNum) && activeIpsNum >= 0 ? activeIpsNum : null;
  const monthly = formatMonthlyCostUsd(activeIps);
  // THE METER, FROM THE LEDGER RATHER THAN FROM THE TIER. GET /cost-preview now
  // carries `stillBilling` and `billingNote`; `tier` is a setting and the
  // active-IP figure is a timestamped measurement, and neither says whether
  // anything is being charged right now. Absent is "not established" and is
  // hedged — see ipamPreviewBillingLine, which this page already renders in the
  // danger zone and which is the ONE spelling of this claim in the frontend.
  // THE CONFIDENCE AXIS, WHICH IS NOT THE DIRECTION. `billingVerified: false` says
  // the verdict above rests on state Prism has stopped refreshing, or on a ledger
  // read that failed and fell back to the config status alone. It never licenses
  // flipping the claim — an unrefreshed row is not evidence the meter stopped —
  // but it does stop this panel presenting either answer as settled.
  //
  // Declared with the lock-out branch far above rather than here, because that
  // branch decides whether this panel renders at all.
  const billChip = ipamCostChip({
    monthlyCost: monthly ?? null,
    stillBilling: costStillBilling,
    verified: costBillingVerified,
  });
  // Whether the FIGURE may be greyed out. Dimming it is this panel saying "this
  // money is historical", and on the degraded path `costStillBilling === false`
  // comes off `status = disabled` — which means a teardown completed, not that the
  // IPAM was deleted. An adopted IPAM survives that and keeps metering, so an
  // unverified stop leaves the number in full navy beside a chip that says it was
  // reported stopped but not verified. Both halves then say the same thing.
  const costMeterSettledStopped = costStillBilling === false && costBillingVerified !== false;
  const meteredAccount = costPreview?.meteredAccount || config.meteredAccount || config.hostAccountId;
  const meteredAccountLocked = costPreview?.meteredAccountLocked !== false;

  // WHEN THE FIGURE ABOVE WAS COUNTED. It is written by estimateActiveIPs from
  // DescribeNetworkInterfaces, and the re-count is allowed to fail WITHOUT
  // failing the refresh — in which case the previous figure stands. The age is
  // therefore the only thing separating a number counted a minute ago from one
  // nobody has managed to re-read in a fortnight, and this panel printed none.
  const activeIpsAt = costPreview?.estimatedActiveIpsAt || config.estimatedActiveIpsAt;

  const previewRows = Array.isArray(preview?.rows) ? preview.rows : [];

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>

      {err && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
        </motion.div>
      )}

      {/* The residual-IPAM banner. Non-dismissible on purpose: a warning that can
          be closed is not a warning while a meter is running. */}
      {residualAlert && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{residualAlert}</Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 2 }}>
            <OperationProgress
              percent={config.progress >= 0 ? config.progress : null}
              label={config.progressLabel || 'VPC IPAM is busy'}
            />
            <Typography sx={hintTextSx}>
              Saving is disabled while another VPC IPAM operation holds the configuration. This
              refreshes on its own.
            </Typography>
          </Paper>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              // replace, not push: a tab change is not a navigation step, and
              // making it one turns Back into "go to the previous tab" instead
              // of leaving Settings.
              setSearchParams({ tab: SLUG_BY_TAB[v] || 'regions' }, { replace: true });
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: `1px solid ${CK.border}`,
              '& .MuiTab-root': {
                fontFamily: FONT_BODY,
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                minHeight: 56,
                color: CK.body,
                '&.Mui-selected': { color: IPAM_COLOR },
              },
              '& .MuiTabs-indicator': { backgroundColor: IPAM_COLOR },
            }}
          >
            <Tab label="Operating regions" />
            <Tab label="Tier & cost" />
            <Tab label="Permissions" />
            <Tab label="Danger zone" />
          </Tabs>

          {/* ---------------- Operating regions ---------------- */}
          {tab === TAB_REGIONS && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Operating Regions</Typography>
              <Typography sx={{ ...bodyTextSx, mb: 1 }}>
                Home Region: <b>{config.homeRegion}</b> (read-only)
              </Typography>
              <Typography sx={{ ...hintTextSx, mb: 2 }}>
                Operating Regions are where IPAM discovers and manages address space. Adding one
                extends every enrolled account&rsquo;s resource discovery into it; removing one stops
                discovery there, and everything found in that Region disappears from the inventory
                even though nothing in your network changes. The home Region is where the IPAM itself
                lives and cannot be removed &mdash; moving it means deleting the IPAM, which destroys
                all address history.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <MultiSelectField
                  label="Operating Regions"
                  options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                  value={regions}
                  onChange={setRegions}
                  lockedValues={config.homeRegion ? [config.homeRegion] : []}
                />
              </Box>

              {!canUpdate && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Operating Regions can only be changed while VPC IPAM is enabled. This
                  configuration is <b>{config.status}</b>.
                </Alert>
              )}

              <Button
                variant="contained"
                disabled={busy || !canUpdate || !regionsChanged}
                onClick={saveRegions}
                sx={ipamButtonSx}
              >
                Save Regions
              </Button>
            </Box>
          )}

          {/* ---------------- Tier & cost ---------------- */}
          {tab === TAB_TIER && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Tier and what it costs</Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontFamily: FONT_TITLE, fontWeight: 600 }}>
                  {IPAM_ADVANCED_PRICE}
                </AlertTitle>
                <Typography sx={{ ...bodyTextSx, color: 'inherit' }}>
                  {IPAM_ADVANCED_REQUIRED_NOTE}
                </Typography>
              </Alert>

              <Box sx={{ ...insetPanelSx, mb: 2 }}>
                <Box sx={{ ...actionRowSx, mb: 1 }}>
                  <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                    Current tier
                  </Typography>
                  <Chip
                    size="small"
                    label={config.tier === TIER_FREE ? 'Free' : 'Advanced'}
                    sx={statusChipSx(config.tier === TIER_FREE ? 'neutral' : 'info')}
                  />
                  <Chip
                    size="small"
                    label={IPAM_ADVANCED_PRICE_MONTHLY}
                    sx={statusChipSx('neutral')}
                  />
                </Box>
                <Box sx={actionRowSx}>
                  <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                    Estimated bill
                  </Typography>
                  {monthly ? (
                    <>
                      <Typography
                        sx={{
                          ...bodyTextSx,
                          fontFamily: FONT_MONO,
                          color: costMeterSettledStopped ? CK.muted : CK.navy,
                        }}
                      >
                        {`≈ ${activeIps.toLocaleString('en-US')} active IPs → ≈ ${monthly} / month`}
                      </Typography>
                      <Chip size="small" label={billChip.label} sx={statusChipSx(billChip.intent)} />
                    </>
                  ) : (
                    <>
                      <Typography sx={{ ...bodyTextSx, fontFamily: FONT_MONO, color: CK.navy }}>
                        &mdash;
                      </Typography>
                      <Chip size="small" label={billChip.label} sx={statusChipSx(billChip.intent)} />
                    </>
                  )}
                </Box>
                <Typography sx={{ ...hintTextSx, mt: 1 }}>
                  {monthly
                    ? 'Counted from attached ENIs in the accounts and Regions enrolled here — a lower '
                      + 'bound. AWS’s own meter may differ.'
                    : 'Prism could not estimate your active-IP count. The charge is real regardless.'}
                </Typography>
                {monthly && (
                  <Typography sx={{ ...hintTextSx, mt: 0.5 }}>
                    {ipamEstimatedBillAge(settingsRelativeTime(activeIpsAt))}
                  </Typography>
                )}
                {/* The ledger's verdict on the meter, in the same spelling the
                    danger zone uses forty lines below. A figure with no verdict
                    beside it reads as money accruing right now. */}
                <Typography sx={{ ...hintTextSx, mt: 1 }}>
                  {/* Both arguments from the COST PREVIEW payload. The danger zone
                      forty lines below renders the same sentence off the TEARDOWN
                      preview, and crossing the two payloads would qualify one
                      reading with another read's confidence. */}
                  {ipamPreviewBillingLine(costStillBilling, costBillingVerified)}
                </Typography>
                {costBillingNote && (
                  <Typography sx={{ ...hintTextSx, mt: 0.5 }}>{costBillingNote}</Typography>
                )}
                <Typography sx={{ ...hintTextSx, mt: 1 }}>{IPAM_PRICE_NOTE}</Typography>
                {costErr && (
                  <Typography sx={{ ...hintTextSx, mt: 1, color: CK.red }}>{costErr}</Typography>
                )}
              </Box>

              <Box sx={{ ...insetPanelSx, mb: 2 }}>
                <Box sx={{ ...actionRowSx, mb: 1 }}>
                  <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                    Who is billed
                  </Typography>
                  <Typography sx={{ ...bodyTextSx, fontFamily: FONT_MONO, color: CK.navy }}>
                    {meteredAccount || '—'}
                  </Typography>
                  {meteredAccountLocked && (
                    <Chip size="small" label="Set by AWS" sx={statusChipSx('neutral')} />
                  )}
                </Box>
                <Typography sx={hintTextSx}>
                  AWS defaults <b>MeteredAccount</b> to <b>ipam-owner</b>, so the account that owns
                  the IPAM is charged for every active IP it manages. Only the accounts enrolled here
                  are managed and billed &mdash; no other account in your organization is affected.
                  Moving the charge to each resource&rsquo;s own account requires AWS Organizations
                  integration and an IPAM created by a delegated administrator, neither of which
                  Prism uses. Prism does not change this setting and cannot change it for you.
                </Typography>
              </Box>

              <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mb: 1 }}>
                Change the tier
              </Typography>
              <FormControl sx={{ mb: 1 }}>
                <RadioGroup
                  value={tierTarget}
                  onChange={(e) => { setTierTarget(e.target.value); setAcknowledged(false); }}
                >
                  <FormControlLabel
                    value={TIER_ADVANCED}
                    control={<Radio sx={{ '&.Mui-checked': { color: IPAM_COLOR } }} />}
                    label={(
                      <Box>
                        <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                          Advanced &mdash; billed
                        </Typography>
                        <Typography sx={hintTextSx}>
                          The only tier that can manage more than one account. Cross-account
                          inventory, overlap detection and utilization all need it.
                        </Typography>
                      </Box>
                    )}
                  />
                  <FormControlLabel
                    value={TIER_FREE}
                    control={<Radio sx={{ '&.Mui-checked': { color: IPAM_COLOR } }} />}
                    label={(
                      <Box>
                        <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                          Free &mdash; single account, single Region
                        </Typography>
                        <Typography sx={hintTextSx}>{IPAM_FREE_TIER_NOTE}</Typography>
                      </Box>
                    )}
                  />
                </RadioGroup>
              </FormControl>

              {tierTarget === TIER_FREE && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle sx={{ fontFamily: FONT_TITLE, fontWeight: 600 }}>
                    Advanced &rarr; Free is not an undo
                  </AlertTitle>
                  <Typography sx={{ ...bodyTextSx, color: 'inherit' }}>
                    AWS refuses the downgrade until every precondition is met &mdash; pools and
                    non-default scopes removed, member resource discoveries disassociated &mdash; so
                    this is a long chain that can fail late, minutes in, on something like
                    &ldquo;pools still exist&rdquo;. It is not a settings save. On the Free tier the
                    cross-account association is refused outright, which ends the inventory, overlap
                    detection and utilization this capability exists for.
                  </Typography>
                </Alert>
              )}

              <FormControlLabel
                control={(
                  <Switch
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: IPAM_COLOR },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: IPAM_COLOR,
                      },
                    }}
                  />
                )}
                label={(
                  <Typography sx={bodyTextSx}>
                    {tierTarget === TIER_FREE
                      ? 'I understand the Free tier cannot manage a second account, and that dropping '
                        + 'to it ends the cross-account inventory, overlap detection and utilization.'
                      : `I understand VPC IPAM Advanced tier is billed at ${IPAM_ADVANCED_PRICE}, that `
                        + 'the meter covers every active IP in the accounts and Regions enrolled here '
                        + `whether or not it sits in an IPAM pool, and that the charge lands on ${meteredAccount || 'the host account'}.`}
                  </Typography>
                )}
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
              />

              {!canUpdate && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The tier can only be changed while VPC IPAM is enabled. This configuration is{' '}
                  <b>{config.status}</b>.
                </Alert>
              )}

              <Button
                variant="contained"
                disabled={busy || !canUpdate || !tierChanged || !acknowledged}
                onClick={saveTier}
                sx={ipamButtonSx}
              >
                {tierTarget === TIER_FREE ? 'Change to Free tier' : 'Change to Advanced tier (billed)'}
              </Button>
            </Box>
          )}

          {/* ---------------- Permissions ---------------- */}
          {tab === TAB_PERMISSIONS && (
            <Box sx={{ p: 3 }}>
              <RolePermissionsPanel
                startScan={startIpamRolesScan}
                getJob={getIpamRolesJob}
                fix={fixIpamRoles}
                capabilityLabel="Amazon VPC IPAM"
              />
            </Box>
          )}

          {/* ---------------- Danger zone: three stages, in-page ---------------- */}
          {tab === TAB_DANGER && (
            <Box sx={{ p: 3 }}>
              {/* Gated on teardown evidence, never on the status alone — see
                  teardownDidNotFinish. And its Retry runs through stage 3 like
                  every other teardown this page dispatches: it selects the
                  ordered mode and opens the typed-customer-ID dialog rather than
                  calling disableIpam from the banner, which is what it used to
                  do. One click, no confirmation, on the page that demands a typed
                  customer ID for the same call ten lines further down, is not a
                  shortcut — it is the guard failing open.

                  AND IT IS HANDED THE PRE-FLIGHT'S OWN METER VERDICT. This screen
                  already reads `preview.stillBilling` — ipamPreviewBillingLine
                  prints it forty lines below — while the banner derived the
                  OPPOSITE from the SHAPE of config.errorMessage: a message that
                  parsed as a per-account map was taken as proof the IPAM was
                  still up, which the settle arm disproves. Two claims about one
                  meter, on one screen, and only one of them was reading the
                  field that exists to answer it.

                  BOTH FIELDS COME FROM THE TEARDOWN PREVIEW, THE PAYLOAD THAT
                  PRODUCED THE VERDICT. `billingVerified` says only whether Prism
                  can confirm that verdict from anything current — never how it was
                  reached, and never which way it points. The cost preview one tab away now carries a
                  `billingVerified` of its own for its own reading, and lending one
                  read's confidence to another read's direction would have this
                  banner vouch for evidence neither of them saw. */}
              {config.status === 'partial_failure' && teardownDidNotFinish && (
                <IpamResidueBanner
                  config={config}
                  job={activeJob}
                  stillBilling={preview?.stillBilling}
                  billingVerified={preview?.billingVerified}
                  busy={busy || operationRunning}
                  onRetry={() => {
                    setMode(MODE_GRACEFUL);
                    setConfirmOpen(true);
                  }}
                />
              )}

              {/* ===== Stage 1 — the pre-flight ledger ===== */}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
                Stage 1 &mdash; what a teardown leaves behind
              </Typography>
              <Typography sx={{ ...bodyTextSx, mb: 2 }}>
                {ipamAdopted
                  ? 'This IPAM is one Prism adopted rather than created, so a teardown here removes '
                    + "Prism's associations and leaves the IPAM itself in place, still metering at "
                    + 'Advanced-tier rates. Read this ledger before you start: some of what it lists '
                    + 'cannot be undone, and most of it Prism will not remove at all.'
                  : 'A teardown that does not finish leaves the IPAM still running and still billing '
                    + 'at Advanced-tier rates across everything you enrolled. Read this ledger before '
                    + 'you start: some of what it lists cannot be undone, and some of it Prism cannot '
                    + 'remove at all.'}
              </Typography>

              {/* The two lines the design requires VERBATIM on this screen —
                  "No VPC is deleted. Your VPCs keep their addresses; only IPAM's
                  record of them goes away." and "All CIDR address history is
                  destroyed, irreversibly." — are rendered here and again inside
                  the stage-3 dialog, but SPELLED ONCE, in ipamCopy.js.
                  Re-typing them per screen is the shape that drifts, and a
                  destruction promise that disagrees with itself between two
                  screens is worse than a price that does.

                  They sit OUTSIDE the preview-dependent block on purpose: they
                  are true whether or not the ledger loaded, and a failed read
                  must not take the two most important sentences down with it. */}
              <Alert severity={ipamAdopted ? 'error' : 'warning'} sx={{ mb: 2 }}>
                <Typography sx={{ ...bodyTextSx, color: 'inherit', fontWeight: 600 }}>
                  {IPAM_NO_VPC_DELETED}
                </Typography>
                {ipamAdopted && (
                  <Typography sx={{ ...bodyTextSx, color: 'inherit', fontWeight: 600 }}>
                    {IPAM_ADOPTED_TEARDOWN_SUMMARY}
                  </Typography>
                )}
                <Typography sx={{ ...bodyTextSx, color: 'inherit', fontWeight: 600 }}>
                  {ipamHistoryDestroyed(ipamAdopted)}
                </Typography>
                <Typography sx={{ ...bodyTextSx, color: 'inherit' }}>
                  {ipamBillingStops(ipamAdopted)}
                </Typography>
                {/* THE PRE-FLIGHT'S OWN VERDICT, WHICH THIS SCREEN IGNORED
                    ENTIRELY. `stillBilling` is a FIELD on IpamTeardownPreview
                    precisely so no client has to derive it — and the one screen
                    that authorises the teardown never read it. Undefined while the
                    ledger has not loaded is a third value, and it is spent as
                    "assume it is billing", never as "no". */}
                <Typography sx={{ ...bodyTextSx, color: 'inherit' }}>
                  {/* Both arguments from the TEARDOWN preview, never from the cost
                      preview above: `billingVerified` qualifies the reading it
                      shipped with, and a confidence borrowed from a different read
                      would vouch for evidence it never saw. */}
                  {ipamPreviewBillingLine(preview?.stillBilling, preview?.billingVerified)}
                </Typography>
              </Alert>

              {previewLoading && !preview && (
                <PortalLoading label="Reading the residue ledger…" color={IPAM_COLOR} py={4} />
              )}

              {previewErr && (
                <Alert severity="error" sx={{ mb: 2 }} action={(
                  <Button color="inherit" size="small" onClick={loadPreview} sx={plainButtonSx}>
                    Retry
                  </Button>
                )}
                >
                  Prism could not build the pre-flight ledger, so this screen cannot list what a
                  teardown would leave behind. ({previewErr})
                </Alert>
              )}

              {previewRows.length > 0 && (
                <TableContainer sx={{ mb: 2, overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={tableHeadRowSx}>
                        <TableCell sx={{ ...headCellSx, width: 150 }}>Verdict</TableCell>
                        <TableCell sx={headCellSx}>What survives the teardown</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 190 }}>Prism can remove it</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewRows.map((row, i) => {
                        const sev = severityMeta(row.severity);
                        const rem = removableMeta(row.removable);
                        const items = Array.isArray(row.items) ? row.items : [];
                        return (
                          <TableRow key={row.key || `residue-${i}`} sx={rowSx(i)}>
                            <TableCell sx={bodyCellSx}>
                              <Tooltip title={sev.hint}>
                                <Chip size="small" label={sev.label} sx={statusChipSx(sev.intent)} />
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                                {row.title}
                              </Typography>
                              <Typography sx={hintTextSx}>{row.detail}</Typography>
                              {/* The named blockers land here — resource id and
                                  account, verbatim from the server. "Pools are
                                  blocked by member resources" is prose an
                                  operator can only nod at; a named id in a named
                                  account is a work item their engineer can close. */}
                              {items.length > 0 && (
                                <Stack component="ul" spacing={0.25} sx={{ pl: 2.5, mt: 0.75, mb: 0 }}>
                                  {items.map((item) => (
                                    <Typography
                                      component="li"
                                      key={item}
                                      sx={{
                                        fontFamily: FONT_MONO,
                                        fontSize: '0.75rem',
                                        color: CK.navy,
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  ))}
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Tooltip title={rem.hint}>
                                <Chip size="small" label={rem.label} sx={statusChipSx(rem.intent)} />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {preview && previewRows.length === 0 && !previewLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The ledger came back empty. That is not the same as &ldquo;nothing survives&rdquo;
                  &mdash; treat it as unknown and re-read it before confirming.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              {/* ===== Stage 2 — the mode ===== */}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
                Stage 2 &mdash; how to tear it down
              </Typography>
              <FormControl sx={{ mb: 1, display: 'block' }}>
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                  <FormControlLabel
                    value={MODE_GRACEFUL}
                    control={<Radio sx={{ '&.Mui-checked': { color: IPAM_COLOR } }} />}
                    label={(
                      <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                          Ordered teardown (default)
                        </Typography>
                        <Typography sx={hintTextSx}>
                          Ten stages, in order: release manual allocations, deprovision pool CIDRs,
                          delete pools and non-default scopes, disassociate every member resource
                          discovery, delete the IPAM, then delete each member&rsquo;s discovery and
                          release its permissions. It stops rather than forcing its way past
                          anything it must not delete, so it can end <b>blocked</b> on a live
                          resource or ask for a force delete. {ipamBillingStops(ipamAdopted)}
                        </Typography>
                      </Box>
                    )}
                  />
                  <FormControlLabel
                    value={MODE_CASCADE}
                    disabled={!cascadeEnabled}
                    control={<Radio sx={{ '&.Mui-checked': { color: CK.red } }} />}
                    label={(
                      <Box sx={{ py: 0.5 }}>
                        <Typography
                          sx={{
                            ...bodyTextSx,
                            color: cascadeEnabled ? CK.red : CK.disabled,
                            fontWeight: 600,
                          }}
                        >
                          Force delete (DeleteIpam --cascade)
                        </Typography>
                        <Typography sx={hintTextSx}>{IPAM_FORCE_DELETE_COPY}</Typography>
                        {/* NOT branched on `ipamAdopted`. Force delete is the one
                            path that DOES delete an adopted IPAM — the operator
                            asks for DeleteIpam --cascade by name — so here the
                            original promise is the true one. */}
                        <Typography sx={{ ...hintTextSx, mt: 0.5 }}>
                          {ipamBillingStops(false)}
                        </Typography>
                      </Box>
                    )}
                  />
                </RadioGroup>
              </FormControl>

              {/* A disabled control carrying its own reason beats an AWS error the
                  operator has to decode — and it is what keeps force delete from
                  ever appearing as a first action. */}
              {cascadeReason && (
                <Alert severity="info" sx={{ mb: 2 }}>{cascadeReason}</Alert>
              )}

              {activeJob && (
                <Box sx={{ ...actionRowSx, mb: 2 }}>
                  <Typography sx={hintTextSx}>Live teardown job:</Typography>
                  <Chip
                    size="small"
                    label={activeJob.status || 'unknown'}
                    sx={statusChipSx(awaitingCascade ? 'warning' : 'info')}
                  />
                  {activeJob.stage && (
                    <Chip
                      size="small"
                      label={`stage ${activeJob.stage}`}
                      sx={statusChipSx('neutral')}
                    />
                  )}
                  {/* `blocked` NO LONGER IMPLIES A BLOCKER. The new settle arm
                      parks the job at `blocked` with an EMPTY list to mean AWS
                      confirmed the IPAM deleted and the pass ran out of graceful
                      budget. An absent chip read as "Prism did not say"; the
                      zero is now stated. */}
                  {blockers.length > 0 ? (
                    <Chip
                      size="small"
                      label={`${blockers.length} blocking ${blockers.length === 1 ? 'resource' : 'resources'}`}
                      sx={statusChipSx('error')}
                    />
                  ) : activeJob.status === IPAM_TEARDOWN_BLOCKED && (
                    <Tooltip title="This teardown is parked, but nothing in your accounts is holding it — there is no resource for you to delete. Press Retry teardown below.">
                      <Chip size="small" label="no blocking resources" sx={statusChipSx('neutral')} />
                    </Tooltip>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* ===== Stage 3 — the typed confirmation ===== */}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
                Stage 3 &mdash; confirm
              </Typography>
              <Typography sx={{ ...bodyTextSx, mb: 2 }}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {mode === MODE_CASCADE
                  ? 'Force delete asks for the customer ID because it destroys address history that '
                    + 'cannot be recovered and deallocates CIDRs from live VPC resources.'
                  : (ipamAdopted
                    ? 'The ordered teardown asks for the customer ID because it strips this customer '
                      + "of every association Prism made, and cannot be undone without re-running the "
                      + 'setup wizard. It does not delete the IPAM and does not stop its meter.'
                    : 'The ordered teardown asks for the customer ID because deleting the IPAM '
                      + 'destroys its address history and cannot be undone.')}
              </Typography>

              <Button
                color="error"
                variant="contained"
                disabled={busy || operationRunning}
                onClick={() => setConfirmOpen(true)}
                sx={plainButtonSx}
              >
                {mode === MODE_CASCADE ? 'Force delete the IPAM' : 'Disable VPC IPAM'}
              </Button>
            </Box>
          )}
        </Paper>
      </motion.div>

      <IpamDestructiveConfirmDialog
        open={confirmOpen}
        customerId={config.customerId}
        title={mode === MODE_CASCADE ? 'Confirm force delete' : 'Confirm teardown'}
        busy={busy}
        blockers={blockers}
        // Only the ORDERED teardown inherits adoption's wording. Force delete
        // really does delete the IPAM and really does destroy the history.
        adopted={ipamAdopted && mode !== MODE_CASCADE}
        confirmLabel={ipamDestructiveConfirmLabel(ipamAdopted && mode !== MODE_CASCADE)}
        onClose={() => setConfirmOpen(false)}
        onConfirm={dispatchTeardown}
        intro={(
          <Typography sx={bodyTextSx}>
            {mode === MODE_CASCADE
              ? 'This runs DeleteIpam --cascade against the IPAM in account '
              : 'This tears down VPC IPAM for this customer, starting with the IPAM in account '}
            <b>{config.hostAccountId || 'the host account'}</b>.
          </Typography>
        )}
      />
    </motion.div>
  );
}
